import { useMutation } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'

import { ApiError, download } from '../api/client'
import { endpoints } from '../api/endpoints'
import { useSession } from '../auth/context'
import { Icon } from '../components/Icon'
import { PageHeader } from '../components/Page'
import { PasswordField } from '../components/PasswordField'
import { errorMessage, t } from '../strings'

/**
 * Hapus akun (ADR-0025), reached from the bottom of Info outlet. The account is closed at once: nobody signs in any more and every
 * device stops; the data stays for the retention period and an operator erases it. Asking for the password again is what keeps a
 * borrowed screen from closing someone's business.
 */
export function DeleteAccountPage() {
  const session = useSession()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [understood, setUnderstood] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const close = useMutation({
    mutationFn: () => endpoints.deleteAccount(password),
    onSuccess: () => {
      // The closed page first (it asks the server nothing), then the local sign-out; the server has already ended every session.
      navigate('/akun-ditutup', { replace: true })
      void session.logout().catch(() => undefined)
    },
    onError: (e) => setFailure(e instanceof ApiError && e.code === 'wrong_password' ? t.deleteWrong : errorMessage(e instanceof ApiError ? e.code : 'network')),
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    setFailure(null)
    if (!understood || !password) return
    close.mutate()
  }

  return (
    <>
      <PageHeader title={t.deleteTitle} subtitle={t.deleteSub} back={{ to: '/pengaturan', label: t.outletInfo }} />
      <div className="narrow">
        <form className="card" onSubmit={submit}>
          <ul className="delete-points">
            {t.deletePoints.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
          <p>
            <Link to="/hapus-akun">{t.deleteMore}</Link>
          </p>

          <p className="muted small">{t.deleteDownloadFirst}</p>
          <button
            type="button"
            className="secondary"
            onClick={() => {
              setDownloadError(null)
              download('/v1/exports/raw.zip', 'gammapos-data.zip').catch((e: unknown) => setDownloadError(errorMessage(e instanceof ApiError ? e.code : 'network')))
            }}
          >
            <Icon name="download" size={20} />
            {t.rawExportButton}
          </button>
          {downloadError && <p className="error">{downloadError}</p>}

          <div className="delete-field">
            <PasswordField label={t.deletePassword} autoComplete="current-password" value={password} onChange={setPassword} />
          </div>
          <label className="check-row">
            <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />
            <span>{t.deleteConfirm}</span>
          </label>
          <button type="submit" className="danger" disabled={close.isPending || !understood || !password}>
            {t.deleteButton}
          </button>
          {failure && (
            <p className="error" role="alert">
              {failure}
            </p>
          )}
        </form>
      </div>
    </>
  )
}

/** Where the owner lands after closing the account: public, so it does not need a session (there is none any more). */
export function AccountClosedPage() {
  return (
    <main className="closed-page">
      <h1>{t.closedTitle}</h1>
      <p>{t.closedBody}</p>
      <p className="muted">{t.closedHelp}</p>
      <p>
        <Link to="/buka-kembali">{t.reopenLink}</Link>
      </p>
      <p>
        <a href="https://gammapos.id/kontak">{t.closedContact}</a>
      </p>
      <p>
        <Link to="/masuk">{t.closedLogin}</Link>
      </p>
    </main>
  )
}
